import styled from '@emotion/styled';

import { OutlineButton, PrimaryButton } from '@/components/common/Button/style';
import { pageSectionLayout } from '@/styles/pageLayout';

export const Container = styled.main`
	${pageSectionLayout}
	display: flex;
	flex-direction: column;
	gap: 3.2rem;
`;

export const HeaderBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const Eyebrow = styled.p`
	color: ${(props) => props.theme.colors.primary400};
	font-size: 1.4rem;
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: 0.12em;
	text-transform: uppercase;
`;

export const Title = styled.h2`
	color: #212529;
	font-size: 3.2rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.tight};
`;

export const Description = styled.p`
	color: #495057;
	font-size: 1.8rem;
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const RegulationTabWrap = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;
	padding: 1.6rem;
	border: 1px solid #dee2e6;
	background: #f8f9fa;
`;

export const RegulationTabButton = styled(OutlineButton)`
	padding: 1.2rem 1.6rem;
	border-color: ${(props) => props.theme.ui.border.default};
	color: ${(props) => props.theme.ui.text.secondary};
	background-color: ${(props) => props.theme.ui.surface.page};

	&.active {
		background-color: ${(props) => props.theme.ui.interactive.primary};
		border-color: ${(props) => props.theme.ui.interactive.primary};
		color: ${(props) => props.theme.ui.text.inverse};
		box-shadow: ${(props) => props.theme.effects.shadowSm};
	}

	&:hover:not(:disabled):not(.active) {
		background-color: ${(props) => props.theme.ui.interactive.primaryMuted};
		border-color: ${(props) => props.theme.ui.interactive.primary};
		color: ${(props) => props.theme.ui.interactive.primary};
	}
`;

export const ContentPanel = styled.section`
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	padding: 2.4rem;
	border: 1px solid #dee2e6;
	background: #fff;
`;

export const RuleTextSection = styled.section`
	display: flex;
	flex-direction: column;
	gap: 2rem;
	padding: 2.4rem;
	border: 1px solid #dee2e6;
	background: #fff;
`;

export const RuleItem = styled.article`
	display: flex;
	flex-direction: column;
	gap: 0.8rem;
	padding-bottom: 2rem;
	border-bottom: 1px solid #f1f3f5;

	&:last-child {
		padding-bottom: 0;
		border-bottom: none;
	}
`;

export const RuleTitle = styled.h4`
	color: #212529;
	font-size: 1.9rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: -0.5px;
`;

export const RuleBody = styled.p`
	color: #495057;
	font-size: 1.7rem;
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: 165%;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	word-break: keep-all;
	white-space: pre-line;
`;

export const RuleLink = styled.a`
	color: ${(props) => props.theme.colors.primary400};
	font-size: inherit;
	font-weight: ${(props) => props.theme.fontWeight.weightMedium};
	line-height: inherit;
	text-decoration: underline;
	text-underline-offset: 0.2rem;

	&:hover {
		color: ${(props) => props.theme.colors.primary400};
	}
`;

export const ContentHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1.2rem;

	@media screen and (max-width: 640px) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

export const ContentTitle = styled.h3`
	color: #212529;
	font-size: 2.4rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const ContentStatus = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 0.6rem 1rem;
	background: #edf2ff;
	color: ${(props) => props.theme.colors.primary400};
	font-size: 1.4rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.lineHeight.tight};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const ContentDescription = styled.p`
	color: #495057;
	font-size: 1.7rem;
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const DownloadButton = styled(PrimaryButton)`
	gap: 0.8rem;
	width: fit-content;
	min-width: 14rem;

	@media screen and (max-width: 640px) {
		width: 100%;
	}
`;

export const DocumentPanel = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1.2rem;
	padding: 1.6rem;
	border: 1px solid #dee2e6;
	border-radius: 0.8rem;
	background: #f8f9fa;

	@media screen and (max-width: 640px) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

export const DocumentName = styled.p`
	color: #212529;
	font-size: 1.6rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	word-break: break-all;
`;

export const DocumentMeta = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 0.4rem 0.8rem;
	border-radius: 999px;
	background: #e7f5ff;
	color: #1864ab;
	font-size: 1.2rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.lineHeight.tight};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const EmptyBox = styled.div`
	display: flex;
	align-items: center;
	padding: 1.6rem;
	border: 1px dashed #ced4da;
	background: #f8f9fa;
	color: #868e96;
	font-size: 1.6rem;
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: ${(props) => props.theme.lineHeight.normal};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;
